import { CreditCard, ArrowLeft, LogOut } from 'lucide-react';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';

import { supabase } from '../../../lib/supabase';
import { useOnboardingStore, BillingStep } from '../../../store/onboarding.store';
import { StepIndicator } from '../components/StepIndicator';
import { BillingDateStep } from './BillingDateStep';
import { LateFeesStep } from './LateFeesStep';
import { ClassAgeAdherenceStep } from './ClassAgeAdherenceStep';
import { HasPaymentProviderStep } from './HasPaymentProviderStep';
import { MigrationStrategyStep } from './MigrationStrategyStep';
import { FeeStructureStep } from './FeeStructureStep';
import { StripeStep } from './StripeStep';
import { CompleteStep } from './CompleteStep';

export default function BillingOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    companyId,
    billingData,
    currentBillingStep,
    setCurrentBillingStep,
    setCurrentSection,
    resetOnboarding,
  } = useOnboardingStore();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/owner-signup');
        return;
      }

      if (!companyId) {
        navigate('/onboarding');
        return;
      }

      // Mark billing section as visited when user navigates to this page
      // This ensures back button navigation works correctly even if user navigated directly
      useOnboardingStore.getState().setBillingSectionVisited(true);

      // Check for Stripe return parameters
      const stripeReturn = searchParams.get('stripe_return');
      if (stripeReturn === 'true') {
        setCurrentBillingStep('stripe-setup');
      }
      
      // If no step set, start at first step
      // Only reset if truly unset - don't reset if user is in the middle of flow
      if (!currentBillingStep) {
        setCurrentBillingStep('has-payment-provider');
      }

      setLoading(false);
    };

    checkAuth();
  }, [navigate, companyId, searchParams, currentBillingStep, setCurrentBillingStep]);

  const handleSignOut = async () => {
    resetOnboarding();
    await supabase.auth.signOut();
    navigate('/');
  };

  // Get current flow path based on selections - Simplified flow
  const getCurrentFlowPath = useCallback((): BillingStep[] => {
    const path: BillingStep[] = [];
    
    // Common steps (always first) - these are always in the same order
    path.push('billing-date', 'late-fees', 'class-age-adherence');
    
    // Q&A flow for provider selection
    path.push('has-payment-provider');
    
    // Handle flow based on hasPaymentProvider answer
    if (billingData.hasPaymentProvider === false) {
      // No provider - new customer path
      // Message: "Ok that makes it easy RollOps can set you up as a new Billing Platform."
      // Flow: fee-structure → stripe-setup → complete
      path.push('fee-structure', 'stripe-setup', 'complete');
    } else if (billingData.hasPaymentProvider === true) {
      // Has provider - transition path
      // Message: "That's ok RollOps makes the Transition Process Super Smooth."
      // Flow: migration-strategy → fee-structure → stripe-setup → complete
      
      // Check if migration strategy is selected
      if (!billingData.rolloverSubscriptionSelection) {
        // Migration strategy not yet selected
        path.push('migration-strategy');
        return path;
      }
      
      // Migration strategy selected - continue to fee structure
      path.push('fee-structure', 'stripe-setup', 'complete');
    } else {
      // hasPaymentProvider is null - path ends at has-payment-provider
      return path;
    }
    
    return path;
  }, [billingData.hasPaymentProvider, billingData.rolloverSubscriptionSelection]);

  const flowPath = useMemo(() => getCurrentFlowPath(), [getCurrentFlowPath]);

  const getCurrentStepIndex = () => {
    // Always check against the actual flow path first
    const index = flowPath.findIndex(s => s === currentBillingStep);
    
      // If step not found, it might be because flow path changed
      // In that case, try to find the step in the common steps (which are always first 3)
      if (index === -1) {
        const commonSteps: BillingStep[] = ['billing-date', 'late-fees', 'class-age-adherence'];
      const commonIndex = commonSteps.indexOf(currentBillingStep);
      if (commonIndex !== -1) {
        // Step is a common step - check if it exists in flow path
        const flowIndex = flowPath.findIndex(s => s === currentBillingStep);
        if (flowIndex !== -1) {
          return flowIndex;
        }
        // If not in flow path but is a common step, it should be added
        // For now, return the common index as fallback
        return commonIndex < flowPath.length ? commonIndex : -1;
      }
    }
    
    return index;
  };

  const goToStep = (step: BillingStep) => {
    setCurrentBillingStep(step);
  };

  const handleNext = () => {
    // For common steps, use explicit navigation to avoid flow path calculation issues
    if (currentBillingStep === 'billing-date') {
      setCurrentBillingStep('late-fees');
      return;
    }
    if (currentBillingStep === 'late-fees') {
      setCurrentBillingStep('class-age-adherence');
      return;
    }
    if (currentBillingStep === 'class-age-adherence') {
      setCurrentBillingStep('has-payment-provider');
      return;
    }
    
    // For other steps, use flow path-based navigation
    const currentIndex = getCurrentStepIndex();
    if (currentIndex === -1) {
      // Step not in flow - try to find next logical step
      const firstIncompleteStep = flowPath.find(step => {
        if (step === 'billing-date' && !billingData.billingStrategy) return true;
        if (step === 'late-fees' && !billingData.lateFeeStrategy) return true;
        if (step === 'class-age-adherence' && billingData.classAgeAdherencePolicy === null) return true;
        if (step === 'has-payment-provider' && billingData.hasPaymentProvider === null) return true;
        if (step === 'migration-strategy' && billingData.hasPaymentProvider === true && !billingData.rolloverSubscriptionSelection) return true;
        if (step === 'fee-structure' && !billingData.feeScheduleId) return true;
        return false;
      });
      if (firstIncompleteStep) {
        setCurrentBillingStep(firstIncompleteStep);
        return;
      }
      // Default to has-payment-provider
      setCurrentBillingStep('has-payment-provider');
      return;
    }
    
    // Normal flow - move to next step in path
    if (currentIndex < flowPath.length - 1) {
      const nextStep = flowPath[currentIndex + 1];
      setCurrentBillingStep(nextStep);
    }
  };

  const handleBack = () => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      setCurrentBillingStep(flowPath[currentIndex - 1]);
    } else {
      setCurrentSection('locations');
      navigate('/onboarding');
    }
  };

  const handleComplete = () => {
    setCurrentSection('subscriptions');
    navigate('/onboarding');
  };

  // Get step labels for indicator
  const getStepLabels = () => {
    return flowPath.map(step => {
      const labels: Record<BillingStep, string> = {
        'billing-date': 'Billing Date',
        'late-fees': 'Late Fees',
        'class-age-adherence': 'Age Policy',
        'has-payment-provider': 'Provider Q&A',
        'migration-strategy': 'Migration',
        'fee-structure': 'Fee Structure',
        'stripe-setup': 'Stripe',
        'complete': 'Review',
      };
      return { id: step, label: labels[step] || step };
    });
  };

  const renderStep = () => {
    switch (currentBillingStep) {
      case 'billing-date':
        return <BillingDateStep onNext={handleNext} onBack={handleBack} />;
      case 'late-fees':
        return <LateFeesStep onNext={handleNext} onBack={handleBack} />;
      case 'class-age-adherence':
        return <ClassAgeAdherenceStep onNext={handleNext} onBack={handleBack} />;
      case 'has-payment-provider':
        return (
          <HasPaymentProviderStep
            onNext={() => {
              if (billingData.hasPaymentProvider === false) {
                // No provider - go directly to fee structure
                setCurrentBillingStep('fee-structure');
              } else if (billingData.hasPaymentProvider === true) {
                // Has provider - go to migration strategy
                setCurrentBillingStep('migration-strategy');
              } else {
                handleNext();
              }
            }}
            onBack={handleBack}
          />
        );
      case 'migration-strategy':
        return (
          <MigrationStrategyStep
            onNext={() => {
              // After migration strategy, go to fee structure
              setCurrentBillingStep('fee-structure');
            }}
            onBack={handleBack}
          />
        );
      case 'fee-structure':
        return (
          <FeeStructureStep
            onNext={() => {
              // After fee structure, go to Stripe setup
              setCurrentBillingStep('stripe-setup');
            }}
            onBack={handleBack}
          />
        );
      case 'stripe-setup':
        return (
          <StripeStep
            onNext={() => {
              // After Stripe setup, go to complete
              setCurrentBillingStep('complete');
            }}
            onBack={handleBack}
          />
        );
      case 'complete':
        return <CompleteStep onComplete={handleComplete} onBack={handleBack} />;
      default:
        // Default to first step in flow
        if (flowPath.length > 0) {
          setCurrentBillingStep(flowPath[0]);
        }
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111010] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#9B1421]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111010] font-['Inter']">
      {/* Header */}
      <header className="border-b border-[#504A4A] bg-[#111010]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/onboarding" className="flex items-center gap-2">
            <ArrowLeft size={20} className="text-[#D1D5DB]" />
            <img src="/rollops-logo.png" alt="RollOps" className="w-10 h-10" />
            <span className="text-2xl font-bold text-[#EBFFEE]">
              Roll<span className="text-rollops-accent">O</span>ps
            </span>
          </Link>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 text-[#D1D5DB] hover:text-[#EBFFEE] transition-colors"
          >
            <LogOut size={18} />
            <span className="text-sm">Sign Out</span>
          </button>
        </div>
      </header>

      {/* Billing Header */}
      <div className="border-b border-[#504A4A] bg-gradient-to-r from-[#9B1421]/10 via-[#111010] to-[#111010]">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center">
              <CreditCard size={56} className="text-rollops-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#EBFFEE]">Billing Setup</h1>
              <p className="text-[#D1D5DB] text-sm">Configure how you&apos;ll accept payments from your members</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="w-full max-w-2xl mx-auto">
          <StepIndicator
            steps={getStepLabels()}
            currentStep={getCurrentStepIndex()}
            onStepClick={(step) => step < getCurrentStepIndex() && goToStep(flowPath[step])}
          />
          <div className="bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
            {renderStep()}
          </div>
        </div>
      </main>
    </div>
  );
}

